import {useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import { Table } from 'components/core-ui/Table'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { toTableRowItems } from './serializers'
import {DeliveryTicketDetails, DeliveryTicketShopModel} from '../../interfaces'
import { useQuery } from 'react-query'
import { formatAndDownloadResponse } from 'utils/helpers'
import { columns, drawerFilters, toolbarActions } from './helpers'
import {TableContainer} from '@mui/material'
import AlertModal from "../../../../../mobile/Common/AlertModal/AlertModal";
import {URLS} from "../../../../../mobile/constants";

const ViewDetails = ({ isHistorical }) => {
  const { showNotification } = useNotification()
  const { id } = useParams()
  const [tableItems, setTableItems] = useState<DeliveryTicketDetails[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [infoData, setInfoData] = useState(null)
  const [isExcelExportLoading, setIsExcelExportLoading] = useState<boolean>(false)
  const [isMetiIntegrationLoading, setIsMetiIntegrationLoading] = useState<boolean>(false)
  const [showMetiFilesButton, setShowMetiFilesButton] = useState<boolean>(false)
  const [shopsThatCanBeSentToMeti, setShopsThatCanBeSentToMeti] = useState<DeliveryTicketShopModel[]>([])
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [shopCode, setShopCode] = useState<string>(null)
  const navigate = useNavigate()

  const [filters, setFilters] = useState([])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {

    setIsLoading(true)
    try {
      const baseUrl = isHistorical
        ? `/shop-reception/deliveryTicketArchive/details/${id}`
        : `/shop-reception/deliveryTicket/details/${id}`

      const hasFilters = tableFilters.some(tableFilter => tableFilter.value)

      const url = toTableGETUrl({
        url: hasFilters ? `${baseUrl}/filter` : baseUrl,
        tableFilters,
        page,
        rowsPerPage,
        order,
        orderBy
      })

      const response = await makeCoreGetRequest(url)

      const {
        depositName,
        expectedDeliveryDate,
        transportCode,
        numberOfSupports,
        validatedSupports,
        externalTicketId
      } = response

      const totalCount = response?.productSupportModelList?.length

      setShopsThatCanBeSentToMeti(response?.shopsThatCanBeSentToMeti)
      setShowMetiFilesButton(response?.showMetiFilesButton)

      setInfoData({
        depositName,
        expectedDeliveryDate,
        transportCode,
        numberOfSupports,
        validatedSupports,
        externalTicketId,
        totalCount
      })

      setTableItems(toTableRowItems(response.productSupportModelList))
      setTotalCount(response.page)
      setIsLoading(false)

      setFilters(response.filters)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const { refetch: exportFileExcel } = useQuery(
    ['exportFiles'],
    async () => {
      setIsExcelExportLoading(true)
      const response = await makeCoreGetFileRequest(
        (isHistorical)
          ? `/shop-reception/deliveryTicketArchive/detailsExport/${id}`
          : `/shop-reception/deliveryTicket/detailsExport/${id}`,
        {
          responseType: 'blob'
        }
      )
      setIsExcelExportLoading(false)
      return formatAndDownloadResponse(response)
    },
    {
      onError: error => {
        setIsExcelExportLoading(false)
        showNotification({
          message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: false
    }
  )

  const sendToMeti  = async (shopCode) => {
    setIsMetiIntegrationLoading(true)
    setIsAlertOpen(true)
    setShopCode(shopCode)
  }

  const doSendToMeti  = async () => {
    try {
      const response = await makeCoreGetFileRequest(
        (isHistorical)
          ? `/shop-reception/deliveryTicketArchive/sendToMeti/${id}/${shopCode}`
          : `/shop-reception/deliveryTicket/sendToMeti/${id}/${shopCode}`,
      )
      setShouldRefreshData(true)
      setIsMetiIntegrationLoading(false)
      setShopsThatCanBeSentToMeti([])

    } catch (err) {
      setIsMetiIntegrationLoading(false)
      showNotification({
        message: `A aparut o eroare la integrarea in METI. ${formatErrorMessage(err)}`,
        type: 'error'
      })
    }
  }

  const metiDetails  = async () => {
    if (shopCodes.length==0) {
      showNotification({
        message: `Cod de magazin invalid!`,
        type: 'error'
      })
      return;
    }

    navigate(
      isHistorical
        ? `/shop-reception/reception/history/metiFiles?ticketExternalId=${infoData.externalTicketId}`
        : `/shop-reception/reception/metiFiles?ticketExternalId=${infoData.externalTicketId}`
    )
  }

  const toolbar = useMemo(() => toolbarActions({
      totalCount: infoData?.totalCount,
      exportFileExcel: exportFileExcel,
      isExcelExportLoading,
      sendToMeti: sendToMeti,
      shopsThatCanBeSentToMeti,
      showMetiFilesButton,
      metiDetails,
      isMetiIntegrationLoading }),
    [totalCount, isExcelExportLoading, isMetiIntegrationLoading])

  const { data: shopCodes, isLoading: isShopCodesLoading } = useQuery(
    ['shop-codes'],
    async () => {
      const data = await makeCoreGetRequest(`/shop-reception/shops/codes`)
      return data.list.map(item => ({
        label: item.displayName,
        value: item.code
      }))
    },
    {
      onError: error => {
        showNotification({
          message: `A apărut o eroare la obtinerea tipurilor de magazine. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  return (
    <>
      <AlertModal
        open={isAlertOpen}
        handleClose={() => setIsAlertOpen(false)}
        title={'Atentie'}
        description={` Au fost recepţionaţi în magazin ${infoData?.validatedSupports} suporţi din ${infoData?.numberOfSupports} aşteptaţi. Sigur integraţi in Meti? Recepţia va fi închisă după integrare! `}
        buttons={[
          {
            label: 'Integrează',
            onClick: () => {
              setIsAlertOpen(false)
              setTimeout(() => {
                doSendToMeti()
              }, 0)
            },
            color: 'primary'
          },
          {
            label: 'Anulează',
            onClick: () => {
              setIsAlertOpen(false)
              setIsMetiIntegrationLoading(false)
              },
            color: 'primary'
          }
        ]}
      />
      <FormInfoItems
        items={[
          { label: 'Bon de livrare:', name: infoData?.externalTicketId },
          { label: 'Depozit sursa:', name: infoData?.depositName },
          { label: 'Data estimata de livrare:', name: infoData?.expectedDeliveryDate },
          { label: 'Cod cursa:', name: infoData?.transportCode },
          { label: 'Numarul de suporti asteptati:', name: infoData?.numberOfSupports },
          { label: 'Numarul de suporti ajungi la destinatie:', name: infoData?.validatedSupports }
        ]}
      />
      <TableContainer style={{ marginTop: 15 }}>
        <Table
          hideCheckbox
          columns={columns}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          filters={drawerFilters(shopCodes, filters)}
          disableSorting
          disablePagination
          toolbarActions={toolbar}
          filtersLabel="Filtreaza"
          filtersButtonLabel="Filtreaza"
          appliedFiltersLabel="Filtre"
          cancelFiltersButtonLabel="Anuleaza"
          applyFiltersButtonLabel="Aplica"
          hasStickyHeader
          maxHeight={600}
        />
      </TableContainer>
    </>
  )
}

export default ViewDetails
